import { Button, Text, Flex, ScrollArea, Badge } from "@radix-ui/themes";
import styles from "./QuackchatSystemMessageConfiguration.module.scss";
import { QuackChatSystemMessageType, quackChatSystemMessageTypes, quackChatSystemMessageTypesMap } from "@/v2/entities/quackchat/Quackchat.model";
import cx from "classnames";
import { useMemo } from "react";
import { EyeOpenIcon, EyeNoneIcon } from "@radix-ui/react-icons";
import { TextWithTooltip } from "@/components/shared/tooltip/TextWithTooltip";
import { GetSystemMessageResponse } from "@/v2/entities/quackchat/Quackchat.api";
import { useIntegrations } from "@/api/integration/Integration.api";
import { isIntegrationCompleted } from "@/api/integration/Integration.model";

export const QuackChatSystemMessageSidebar = ({
    selectedMessageType,
    handleMessageTypeSelected,
    allowToggle,
    systemMessageMap,
}: {
    selectedMessageType: QuackChatSystemMessageType | undefined;
    handleMessageTypeSelected: (type: QuackChatSystemMessageType) => void;
    allowToggle: boolean;
    systemMessageMap: Map<QuackChatSystemMessageType, GetSystemMessageResponse>;
}) => {
    const { data: integrations } = useIntegrations();

    const messageTypes = useMemo(() => {
        const isSalesforceLiveChatConnected = integrations?.some(
            (i) =>
                i.name === "salesforce_livechat" &&
                i.type === "CHAT" &&
                isIntegrationCompleted(i)
        );

        return isSalesforceLiveChatConnected
            ? quackChatSystemMessageTypes
            : quackChatSystemMessageTypes.filter(
                  (type) => type !== "CONNECTED_TO_LIVE_AGENT"
              );
    }, [integrations]);

    return (
        <ScrollArea
            style={{ width: "400px", height: "100%" }}
            scrollbars={"vertical"}
        >
            <Flex direction={"column"} gap={"24px"} className={styles.Sidebar}>
                <Text size={"7"} color={"gray"} weight={"medium"} highContrast>
                    All Messages
                </Text>

                <Flex pb={"24px"} direction={"column"} gap={"1"}>
                    {messageTypes.map((type) => {
                        const { enabled } = systemMessageMap.get(type) ?? {
                            enabled: true,
                        };

                        return (
                            <Button
                                key={type}
                                size={"1"}
                                color={"gray"}
                                variant={"soft"}
                                highContrast
                                className={cx(styles.SidebarItem, {
                                    [styles.isActive]: selectedMessageType === type,
                                })}
                                onClick={() => handleMessageTypeSelected(type)}
                            >
                                <Flex width={"100%"} justify={"between"} gap={"1"}>
                                    <TextWithTooltip
                                        size={"2"}
                                        color={"gray"}
                                        weight={"medium"}
                                        highContrast
                                        truncate
                                    >
                                        {quackChatSystemMessageTypesMap[type].title}
                                    </TextWithTooltip>

                                    {allowToggle && (
                                        <>
                                            {enabled ? (
                                                <Badge color={"green"} size={"1"}>
                                                    <EyeOpenIcon />
                                                    Enabled
                                                </Badge>
                                            ) : (
                                                <Badge color={"gray"} size={"1"}>
                                                    <EyeNoneIcon />
                                                    Disabled
                                                </Badge>
                                            )}
                                        </>
                                    )}
                                </Flex>

                                <Text
                                    size={"2"}
                                    color={"gray"}
                                    truncate
                                    className={styles.Description}
                                >
                                    {quackChatSystemMessageTypesMap[type].description}
                                </Text>
                            </Button>
                        );
                    })}
                </Flex>
            </Flex>
        </ScrollArea>
    );
};
